// clientReportPdf.js — the ONE way the client's report page makes the sender's PDF.
// ---------------------------------------------------------------------------
// v0.292.0. The shared report page (/r/<token>) offers the PDF two ways: as a
// download, and read in the page ("View as PDF"). Brett, v0.218.0: "what we
// produce is what they get." Both go through buildClientReportPdf() below, so
// the bytes a client reads on screen are the bytes they download, and neither
// can quietly drift from the sender's own report.
//
// A build is slow (every photo and plan is fetched, then jsPDF lays the pages
// out), so a built PDF is kept and reused while its INPUTS are unchanged. The
// fingerprint is a small hash over everything that feeds the PDF — the
// sender's settings and plan level, the brand, the project, the drawings and
// items as the PDF reads them, the distribution list and today's date — not
// over the page's chat or presence state, which the PDF never prints. Toggling
// web page <-> PDF reuses the build; a reply arriving does not rebuild it; a
// changed input does.
//
// Pure where it can be (node-testable): the browser-only parts (jsPDF, image
// fetching, the download) arrive through `deps` and dynamic imports.
// ---------------------------------------------------------------------------
import { clientReportSettings } from './report-web-look.js';
import { normaliseDrawing, normaliseDefect } from './reportPdf.js';

/**
 * cyrb53 — a small, fast 53-bit string hash (public domain, bryc). Stable
 * across runs and platforms; collisions are astronomically unlikely for the
 * handful of fingerprints one page ever makes. Returns a hex string.
 */
export function cyrb53(str, seed = 0) {
  let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

/** The same hash over bytes (ArrayBuffer or Uint8Array) — for proving two copies are one PDF. */
export function bytesHash(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < u8.length; i++) {
    h1 = Math.imul(h1 ^ u8[i], 2654435761);
    h2 = Math.imul(h2 ^ u8[i], 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0).toString(16).padStart(8, '0') + (h1 >>> 0).toString(16).padStart(8, '0');
}

/** JSON with every object's keys sorted, so the same facts in another order hash the same. */
export function stableJson(value) {
  return JSON.stringify(value, (k, v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.keys(v).sort().reduce((o, key) => { if (v[key] !== undefined) o[key] = v[key]; return o; }, {});
    }
    return v;
  });
}

const dayOf = (d) => {
  const t = d instanceof Date && !Number.isNaN(d.getTime()) ? d : new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

/**
 * Everything the PDF is built from, as the PDF reads it. The one place the
 * page's payload meets the PDF's inputs — Download and the in-page view both
 * come through here.
 */
export function pdfBuildInputs(data, now = new Date()) {
  const d = data || {};
  // v0.291.0 — the same settings the page's look is drawn from (one rule, report-web-look.js)
  const { settings, tier, brand } = clientReportSettings(d.pdf);
  const project = d.project || {};
  return {
    settings,
    tier,
    brand,
    project,
    drawings: (d.drawings || []).map(normaliseDrawing),
    defects: (d.defects || []).map(normaliseDefect),
    reportTeamMembers: Array.isArray(d.reportTeamMembers) ? d.reportTeamMembers : [],
    // the cover and every page header print today's date
    reportDate: dayOf(now),
  };
}

/** A short stable id of a build's inputs; equal fingerprints = the same PDF. */
export function reportPdfFingerprint(data, now = new Date()) {
  return cyrb53(stableJson(pdfBuildInputs(data, now)));
}

/** DefectReport_<project>.pdf — what the sender's own download is called too. */
export function pdfFileName(data) {
  const name = (data && data.project && data.project.name) || 'project';
  return `DefectReport_${String(name).replace(/[^A-Za-z0-9_-]+/g, '_')}.pdf`;
}

// The heavy parts load only when a PDF is actually wanted, so the page itself
// stays light (the sender's template pulls in jsPDF).
async function defaultDeps() {
  const [{ buildDefectReportPdf }, { loadReportImages }] = await Promise.all([
    import('./defect-report-template.js'),
    import('./reportImages.js'),
  ]);
  return { buildDefectReportPdf, loadReportImages, imageDeps: undefined };
}

/**
 * A seam for the headless checks only: when set, every build uses these deps
 * instead of the real template and image loader. Never set in the app.
 */
export const testSeam = { deps: null };

/**
 * Build the sender's PDF for this page's report.
 * Returns { bytes: ArrayBuffer, fileName, problems, fingerprint, hash, pages? }.
 * `bytes` is the finished file — hand a COPY to anything that transfers
 * buffers (pdf.js does), so the download still has them.
 */
export async function buildClientReportPdf(data, { onProgress, deps = null, now = new Date() } = {}) {
  const D = deps || testSeam.deps || await defaultDeps();
  const inputs = pdfBuildInputs(data, now);
  const prepared = await D.loadReportImages(
    { drawings: inputs.drawings, defects: inputs.defects, logoUrl: inputs.brand.logoDataUrl || null },
    D.imageDeps,
    { onProgress },
  );
  const doc = await D.buildDefectReportPdf({
    project: { ...inputs.project, CustomerName: inputs.project.customerName },
    drawings: prepared.drawings,
    defects: prepared.defects,
    brand: { ...inputs.brand, logoDataUrl: prepared.logo },
    reportTeamMembers: inputs.reportTeamMembers,
    settings: { ...inputs.settings, tier: inputs.tier },
  });
  const bytes = doc.output('arraybuffer');
  return {
    bytes,
    fileName: pdfFileName(data),
    problems: Array.isArray(prepared.problems) ? prepared.problems : [],
    fingerprint: cyrb53(stableJson(inputs)),
    hash: bytesHash(bytes),
  };
}

/**
 * One build per set of inputs. get(data) returns the finished build when the
 * fingerprint matches, joins a build already running for it, and otherwise
 * starts one. A failed build is dropped so the next get() tries again.
 */
export function reportPdfCache() {
  let done = null;      // the last finished build
  let running = null;   // { fingerprint, promise }
  return {
    peek(data, now) {
      const fp = reportPdfFingerprint(data, now);
      return done && done.fingerprint === fp ? done : null;
    },
    get(data, opts = {}) {
      const now = opts.now || new Date();
      const fp = reportPdfFingerprint(data, now);
      if (done && done.fingerprint === fp) return Promise.resolve(done);
      if (running && running.fingerprint === fp) return running.promise;
      const promise = buildClientReportPdf(data, { ...opts, now }).then(
        (out) => { done = out; if (running && running.promise === promise) running = null; return out; },
        (e) => { if (running && running.promise === promise) running = null; throw e; },
      );
      running = { fingerprint: fp, promise };
      return promise;
    },
    clear() { done = null; running = null; },
  };
}

/** Save the bytes as a file in the browser (the same bytes the page shows). */
export function downloadPdfBytes(bytes, fileName) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = fileName || 'report.pdf'; a.rel = 'noopener'; a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
